import { FormikHelpers } from "formik";
import { TFunction } from "i18next";
import { List, Map } from "immutable";
import * as yup from "yup";
import { OpenRightPanel } from "../../../../types/common/rightPanel";
import { deriveInboundStatus } from "../../../../utils/inboundShipment";
import { toastResponseError } from "../../../../utils/responseMessageHelper";

export interface InboundAddressOption {
  label: string;
  value: number;
}

export interface InboundPackageFormValues {
  weight: string | number;
  height: string | number;
  length: string | number;
  width: string | number;
  customerComment: string;
}

export interface InboundShipmentFormValues {
  id: number | null;
  packages: InboundPackageFormValues[];
  address: InboundAddressOption | null;
}

export const emptyInboundPackage = (): InboundPackageFormValues => ({
  weight: "",
  height: "",
  length: "",
  width: "",
  customerComment: "",
});

// react-select needs a plain array — an Immutable List breaks its menu.
export const buildInboundAddressOptions = (
  addresses: any,
): InboundAddressOption[] =>
  addresses
    .map((address: any) => ({
      label: `${address.get("name")} — ${address.get("city")}, ${address.get(
        "state",
      )}`,
      value: address.get("id"),
    }))
    .toArray();

const SENDER_MATCH_FIELDS = [
  "name",
  "phone",
  "address_line_1",
  "address_line_2",
  "city",
  "state",
  "zip_code",
];

// The show serializer snapshots the sender into sender_* fields; when it
// carries no inbound_address_id, the only way to preselect is an exact match.
const addressMatchesSender = (address: any, shipment: any) =>
  SENDER_MATCH_FIELDS.every(
    (field) =>
      (address.get(field) || "") === (shipment.get(`sender_${field}`) || ""),
  );

const findInitialAddressOption = (
  panelData: any,
  shipment: any,
  addresses: any,
  options: any,
): InboundAddressOption | null => {
  const preselectAddressId = panelData.get("preselectAddressId");
  if (preselectAddressId) {
    return (
      options.find((o: InboundAddressOption) => o.value === preselectAddressId) ||
      null
    );
  }
  if (!shipment) return null;

  const addressId = shipment.get("inbound_address_id");
  if (addressId) {
    return (
      options.find((o: InboundAddressOption) => o.value === addressId) || null
    );
  }

  const matched = addresses.find((address: any) =>
    addressMatchesSender(address, shipment),
  );
  return matched
    ? options.find(
        (o: InboundAddressOption) => o.value === matched.get("id"),
      ) || null
    : null;
};

// Sending `packages` is a wholesale replace on the backend and destroys any
// persisted estimate; labeled shipments never submit (the form is read-only).
export const buildInboundShipmentPayload = (
  values: InboundShipmentFormValues,
) => ({
  inbound_shipment: {
    packages: values.packages.map((pkg) => ({
      weight: Number(pkg.weight),
      height: Number(pkg.height),
      length: Number(pkg.length),
      width: Number(pkg.width),
      customer_comment: pkg.customerComment,
    })),
    inbound_address_id: values.address ? values.address.value : null,
  },
});

export const inboundShipmentFormik = (
  t: TFunction,
  panelData: any,
  shipment: any,
  addresses: any,
  createInboundShipment: (data: { inbound_shipment: any }) => Promise<any>,
  updateInboundShipment: (
    id: number,
    data: { inbound_shipment: any },
  ) => Promise<any>,
  getInboundShipment: (id: number) => Promise<any>,
  openRightPanel: OpenRightPanel,
) => {
  const isLabeled = deriveInboundStatus(shipment) === "labeled";

  const getInitialValues = (): InboundShipmentFormValues => {
    const options = buildInboundAddressOptions(addresses);
    const packages = shipment
      ? shipment
          .get("packages", List())
          .sortBy((pkg: any) => pkg.get("sequence"))
          .map(
            (pkg: any): InboundPackageFormValues => ({
              weight: pkg.get("weight", "") as string,
              height: pkg.get("height", "") as string,
              length: pkg.get("length", "") as string,
              width: pkg.get("width", "") as string,
              customerComment: (pkg.get("customer_comment") || "") as string,
            }),
          )
          .toArray()
      : [];

    return {
      id: shipment ? (shipment.get("id") as number) : null,
      packages: packages.length ? packages : [emptyInboundPackage()],
      address: findInitialAddressOption(panelData, shipment, addresses, options),
    };
  };

  const dimensionSchema = (fieldKey: string) =>
    yup
      .number()
      // an empty input must read as "required", not as a failed number cast
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value,
      )
      .typeError(t("error.invalid", { field: t(fieldKey) }))
      // backend rounds to 2 dp and requires 0.01–999999.99
      .min(0.01, t("error.invalid", { field: t(fieldKey) }))
      .max(999999.99, t("error.invalid", { field: t(fieldKey) }))
      .required(t("error.required", { field: t(fieldKey) }));

  return {
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: false,
    initialValues: getInitialValues(),
    validationSchema: () =>
      yup.object().shape({
        packages: yup
          .array()
          .of(
            yup.object().shape({
              weight: dimensionSchema("inbound.form.weight"),
              height: dimensionSchema("inbound.form.height"),
              length: dimensionSchema("inbound.form.length"),
              width: dimensionSchema("inbound.form.width"),
              customerComment: yup.string(),
            }),
          )
          .min(1),
        address: isLabeled
          ? yup.object().nullable()
          : yup
              .object()
              .nullable()
              .required(
                t("error.required", { field: t("inbound.form.address") }),
              ),
      }),
    onSubmit: (
      values: InboundShipmentFormValues,
      { setSubmitting }: FormikHelpers<InboundShipmentFormValues>,
    ) => {
      // Packages (and the sender) are immutable once a label exists — the
      // submit button is hidden, this guard is belt-and-braces.
      if (isLabeled) {
        setSubmitting(false);
        return undefined;
      }

      const payload = buildInboundShipmentPayload(values);

      if (values.id) {
        return updateInboundShipment(values.id, payload)
          .then(() => getInboundShipment(values.id as number))
          .catch((response: Error) => toastResponseError(response))
          .finally(() => setSubmitting(false));
      }

      return createInboundShipment(payload)
        .then((resp) => {
          const created = resp.value.get("data");
          openRightPanel("INBOUND_SHIPMENT", Map({ id: created.get("id") }));
        })
        .catch((response: Error) => toastResponseError(response))
        .finally(() => setSubmitting(false));
    },
  };
};
